import { solutionProducts, hiddenProductIds } from '@/mocks/homeData';
import { useInView } from '@/hooks/useInView';

const visibleProducts = solutionProducts.filter((product) => !hiddenProductIds.includes(product.id));

function SwivelCard({ product, delay }: { product: typeof solutionProducts[0]; delay: number }) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`w-full flex-none sm:w-[calc((100%-20px)/2)] md:w-[calc((100%-24px)/2)] lg:w-[calc((100%-72px)/4)] transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="group [perspective:1000px]">
        <div className="relative h-[320px] md:h-[340px] transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
          {/* Front */}
          <div className="absolute inset-0 [backface-visibility:hidden] flex flex-col rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden">
            {/* Top icon area */}
            <div className={`${product.iconBg} p-6 flex items-center justify-center`}>
              <div className="w-14 h-14 rounded-xl bg-background-50/25 flex items-center justify-center">
                <i className={`${product.icon} text-2xl text-background-50`} />
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 p-5 flex flex-col bg-[#F0F6FC]">
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">
                {product.name}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                {product.shortDesc}
              </p>
              <div className="flex items-center gap-1 text-sm font-medium text-primary-600 mt-3">
                <span>Learn more</span>
                <i className="ri-arrow-right-line" />
              </div>
            </div>
          </div>

          {/* Back */}
          <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-2xl bg-foreground-900 text-background-50 p-6 flex flex-col">
            <div className="w-10 h-10 rounded-lg bg-background-50/15 flex items-center justify-center mb-4">
              <i className={`${product.icon} text-xl`} />
            </div>
            <h3 className="text-lg font-semibold mb-3">{product.name}</h3>
            <p className="text-sm text-background-300 leading-relaxed flex-1">
              {product.fullDesc}
            </p>
            <a
              href="#"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors mt-4"
            >
              Explore product
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SolutionCards() {
  const [titleRef, titleInView] = useInView<HTMLDivElement>();

  return (
    <section id="solutions" className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-background-50">
      {/* Header */}
      <div
        ref={titleRef}
        className={`flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14 transition-all duration-700 ${
          titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-100 rounded-full mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            <span className="text-xs font-medium text-accent-700 uppercase tracking-wider">Products</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight">
            Solutions across the Ardeun platform
          </h2>
        </div>
        <p className="text-base text-foreground-600 max-w-md md:text-right">
          Integrated biometric products designed to secure identity, communication, and access across your entire digital infrastructure.
        </p>
      </div>

      {/* Cards Grid */}
      <div className="flex flex-wrap justify-center gap-5 md:gap-6 max-w-[1500px] mx-auto">
        {visibleProducts.map((product, i) => (
          <SwivelCard key={product.id} product={product} delay={i * 80} />
        ))}
      </div>
    </section>
  );
}